<template>
  <main-layout :hide-background="true" :hide-wrapper="true" :hide-title="true">
    <div class="tw-px-2 tw-pt-2 lg:tw-p-2 lg:tw-py-4">
      <!-- One full-width search above all content; results open in a dropdown -->
      <div class="tw-mb-6">
        <knowledge-base-search large :autofocus="showHero" />
      </div>

      <div
        class="tw-grid tw-grid-cols-1 tw-items-start tw-gap-6 lg:tw-grid-cols-7"
      >
        <!-- Left: Categories (desktop) -->
        <div class="tw-space-y-6 lg:tw-col-span-2">
          <knowledge-base-categories :items="knowledgeBaseMenu" />

          <faq-categories-nav />
        </div>

        <main class="lg:tw-col-span-5">
          <!-- Mobile: categories toggle -->
          <c-button
            class="tw-mb-4 lg:tw-hidden"
            outline
            icon="fa-duotone fa-list"
            label="Categorieën"
            @click="openMobileCategories"
          />

          <div :class="showCard ? cardClass : undefined">
            <h2
              v-if="showHero"
              class="tw-m-0 tw-mb-6 tw-text-center tw-text-3xl tw-font-bold tw-text-gray-900 lg:tw-text-4xl"
            >
              Hallo, hoe kunnen we je helpen?
            </h2>

            <router-view v-slot="{ Component, route: matchedRoute }">
              <component
                :is="Component"
                :key="matchedRoute.params.slug || matchedRoute.path"
              />
            </router-view>
          </div>
        </main>
      </div>
    </div>

    <!-- Mobile categories menu -->
    <knowledge-base-categories-mobile
      ref="kbMobileRef"
      :items="knowledgeBaseMenu"
    />
  </main-layout>
</template>

<script setup lang="ts">
import FaqCategoriesNav from "components/knowledge-base/faq/FaqCategoriesNav.vue";
import KnowledgeBaseCategories from "components/knowledge-base/KnowledgeBaseCategories.vue";
import KnowledgeBaseCategoriesMobile from "components/knowledge-base/KnowledgeBaseCategoriesMobile.vue";
import KnowledgeBaseSearch from "components/knowledge-base/KnowledgeBaseSearch.vue";
import { orderedCategoryChildren } from "composables/knowledge-base/useKnowledgeBaseCategoryMenu";
import type { KnowledgeBaseItemStub } from "models/knowledge-base/knowledge-base";
import { storeToRefs } from "pinia";
import { useFaqStore } from "store/modules/faqStore";
import { useKnowledgeBaseStore } from "store/modules/knowledgeBaseStore";
import { computed, onBeforeMount, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import MainLayout from "./MainLayout.vue";

const route = useRoute();
const router = useRouter();

const knowledgeBaseStore = useKnowledgeBaseStore();
const { categories } = storeToRefs(knowledgeBaseStore);
const { fetchAll } = knowledgeBaseStore;

const faqStore = useFaqStore();
const { selectedCategoryUuid } = storeToRefs(faqStore);

const cardClass =
  "tw-rounded-2xl tw-border tw-border-gray-100 tw-bg-white tw-p-6 lg:tw-p-12";

// The centered greeting is the homepage state; the search bar itself is shown
// on every knowledge base page.
const showHero = computed(
  () =>
    route.name === "knowledgeBaseIndex" && selectedCategoryUuid.value === null,
);

const isArticleView = computed(() =>
  route.matched.some(
    (matchedRoute) => matchedRoute.name === "knowledgeBaseItem",
  ),
);

// The article view draws its own cards (article + related). Everything else —
// the home overview and an open FAQ section — sits inside a single card.
const showCard = computed(() => isArticleView.value === false);

onBeforeMount(async () => {
  await fetchAll();
});

const kbMobileRef = useTemplateRef("kbMobileRef");

const openMobileCategories = () => {
  kbMobileRef.value?.open();
};

const buildItemNode = ({
  uuid,
  title,
  slug,
  is_published,
}: KnowledgeBaseItemStub) => ({
  uuid,
  meta: {
    title,
    exactRouteIdentifier: slug,
    ...(!is_published && {
      badge: {
        key: "notifications",
        label: "concept",
        color: "grey-4",
        textColor: "dark",
      },
    }),
  },
  path: `/knowledge-base/${slug}`,
  action: () => {
    router.push({
      name: "knowledgeBaseItem",
      params: { slug },
    });
  },
});

const knowledgeBaseMenu = computed(() =>
  (categories.value || []).map((cat) => ({
    key: cat.uuid,
    meta: { title: cat.title },
    children: orderedCategoryChildren(cat).map((child) =>
      child.kind === "item"
        ? buildItemNode(child.item)
        : {
            key: child.subcategory.uuid,
            meta: { title: child.subcategory.title },
            children: (child.subcategory.items || []).map(buildItemNode),
          },
    ),
  })),
);

// Keep MenuLink exact highlighting in sync with the slug
watch(
  () => route.params.slug,
  (slug) => {
    // Set exactRouteIdentifier on all matched routes (parent and children)
    // This ensures active state works even on child routes like /edit
    route.matched.forEach((matchedRoute) => {
      const meta = matchedRoute.meta as { exactRouteIdentifier?: string };
      if (typeof slug === "string" && slug.length > 0) {
        meta.exactRouteIdentifier = slug;
      } else {
        meta.exactRouteIdentifier = undefined;
      }
    });
  },
  { immediate: true },
);
</script>
